import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import { apiService } from "../api/apiService";

// Fetch bot errors
export const fetchBotErrors = createAsyncThunk(
    'botErrors/fetchBotErrors',
    async (botId, { rejectWithValue }) => {
        try {
            const data = await apiService.getBotErrors(botId);

            return {
                botId,
                success: true,
                errors: data?.errors || [],
                message: 'Bot errors fetched successfully'
            };
        }
        catch (error) {
            return rejectWithValue({
                botId,
                message: error.message
            });
        }
    }
);

const initialState = {
    botErrors: {}, // Store errors per bot ID
    loading: 'idle',
    error: null,
    message: null
};

const botErrorsSlice = createSlice({
    name: 'botErrors',
    initialState,
    reducers: {
        resetBotErrors(state) {
            state.botErrors = {};
            state.loading = 'idle';
            state.error = null;
            state.message = null;
        },
        clearError(state) {
            state.error = null;
        },
        clearMessage(state) {
            state.message = null;
        },
        clearBotErrors(state, action) {
            const botId = action.payload;
            if (state.botErrors[botId]) {
                delete state.botErrors[botId];
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch bot errors
            .addCase(fetchBotErrors.pending, (state, action) => {
                const botId = action.meta.arg;
                state.loading = 'pending';
                state.error = null;
                state.message = null;
                
                // Initialize bot errors if not exists
                if (!state.botErrors[botId]) {
                    state.botErrors[botId] = {
                        errors: [],
                        loading: 'pending',
                        error: null,
                        message: null
                    };
                } else {
                    state.botErrors[botId].loading = 'pending';
                    state.botErrors[botId].error = null;
                }
            })
            .addCase(fetchBotErrors.fulfilled, (state, action) => {
                const { botId, errors, message } = action.payload;
                state.loading = 'idle';
                state.error = null;
                
                if (!state.botErrors[botId]) {
                    state.botErrors[botId] = {};
                }
                
                state.botErrors[botId] = {
                    errors,
                    loading: 'idle',
                    error: null,
                    message
                };
            })
            .addCase(fetchBotErrors.rejected, (state, action) => {
                const { botId, message } = action.payload || { botId: action.meta.arg, message: action.error.message };
                state.loading = 'idle';
                state.error = message;
                
                if (!state.botErrors[botId]) {
                    state.botErrors[botId] = {};
                }
                
                state.botErrors[botId] = {
                    errors: [],
                    loading: 'idle',
                    error: message,
                    message: null
                };
            });
    }
});

export const { resetBotErrors, clearError, clearMessage, clearBotErrors } = botErrorsSlice.actions;

// Selectors
export const selectBotErrors = createSelector(
  [state => state.botErrors.botErrors, (state, botId) => botId],
  (botErrors, botId) => botErrors[botId] || { errors: [], loading: 'idle', error: null }
);
export const selectBotErrorsLoading = (state) => state.botErrors.loading;
export const selectBotErrorsError = (state) => state.botErrors.error;

export default botErrorsSlice.reducer; 